import { ArrowRight } from 'lucide-react'
import Link from 'next/link'
import type { HubKind } from '@dupe/generative-ui'
import type { PublishedResearchHubSibling } from '~/src/db/queryPublishedResearchHub'
import { cn } from '~/src/lib/utils'

export interface HubSiblingBandProps {
  sibling: PublishedResearchHubSibling
  variant: 'top' | 'bottom'
  onNavigate?: (sibling: PublishedResearchHubSibling) => void
}

interface SiblingCopy {
  kicker: string
  label: string
  support: string
}

// Copy depends on which hub the reader is heading toward (the sibling's kind),
// not the current page's kind. Transactional sibling = "go shop"; informational
// sibling = "go learn".
function siblingCopy(siblingKind: HubKind): SiblingCopy {
  if (siblingKind === 'transactional') {
    return {
      kicker: 'Ready to choose?',
      label: 'See our top picks',
      support:
        'Compare the highest-rated options with prices and where to buy.',
    }
  }
  return {
    kicker: 'Want the full picture?',
    label: 'Read the complete guide',
    support: 'Understand the key factors and trade-offs before you decide.',
  }
}

/**
 * Deterministic cross-link between the two hubs in a cluster. Rendered by
 * {@link ReportHubRenderer} (not by the agent), so the informational and
 * transactional hubs always reference each other near the top and bottom of the
 * page. Only mounted when the sibling is actually published+public.
 */
export function HubSiblingBand({
  sibling,
  variant,
  onNavigate,
}: HubSiblingBandProps) {
  const copy = siblingCopy(sibling.hubKind)

  if (variant === 'top') {
    return (
      <Link
        href={sibling.path}
        onClick={() => onNavigate?.(sibling)}
        className={cn(
          'group flex items-center justify-between gap-3 rounded-2xl border border-info-100 bg-info-50 px-5 py-3',
          'transition-colors hover:border-info-200'
        )}
      >
        <span className='flex min-w-0 flex-col'>
          <span className='text-xs font-semibold uppercase tracking-wide text-info-700'>
            {copy.kicker}
          </span>
          <span className='truncate text-base font-semibold text-foreground'>
            {copy.label}: {sibling.title}
          </span>
        </span>
        <ArrowRight
          className='size-5 shrink-0 text-info-700 transition-transform group-hover:translate-x-0.5'
          aria-hidden
        />
      </Link>
    )
  }

  return (
    <section className='flex flex-col items-start gap-4 rounded-2xl border border-info-100 bg-info-50 px-6 py-6 md:flex-row md:items-center md:justify-between md:px-8'>
      <div className='flex min-w-0 flex-col gap-1'>
        <p className='text-xs font-semibold uppercase tracking-wide text-info-700'>
          {copy.kicker}
        </p>
        <h2 className='text-balance font-display text-xl font-semibold leading-7 text-foreground md:text-2xl md:leading-8'>
          {sibling.title}
        </h2>
        <p className='text-sm leading-5 text-foreground-secondary'>
          {copy.support}
        </p>
      </div>
      <Link
        href={sibling.path}
        onClick={() => onNavigate?.(sibling)}
        className='inline-flex shrink-0 items-center gap-1 rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background hover:opacity-90'
      >
        {copy.label}
        <ArrowRight className='size-4' aria-hidden />
      </Link>
    </section>
  )
}
